import React, { useState, useEffect } from "react";
import { Select, Button, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchShipOwners, fetchVessels, fetchCasperID, fetchVesselLocations } from "../../features/RawData/RawDataSlice";
import { fetchDocSuggestions } from "../../features/VesselsAnalysis/VesselsAnalysisSlice";
import { getSettlementDues } from "../../features/EUA/EuaSlice";
import MetricCards from "./MetricCards";
import ETSExposure from "./ETSExposure";
import EmissionTrend from "./EmissionTrend";
import BarChartSection from "../../container/chart/BarChart";

const { Option } = Select;

const currentYear = new Date().getFullYear();
const yearoptions = Array.from({ length: currentYear - 2023 + 1 }, (_, i) => {
  const year = 2023 + i;
  return { value: year.toString(), label: year.toString() };
});

const ComplianceOverview = ({ data }) => {
  const dispatch = useDispatch();

  const { shipOwners, shipOwnersStatus, vesselDetails, vesselStatus, casperID } = useSelector((state) => state.rawData);
  const { duesGraph } = useSelector((state) => state.eua); // ← single source of truth for chart
  const { docSuggestions, docSuggestionsStatus } = useSelector((state) => state.vesselsAnalysis);

  const [docValue,   setDocValue]   = useState("");
  const [shipOwner,  setShipOwner]  = useState("");
  const [vesselName, setVesselName] = useState("");
  const [iMONumber,  setIMONumber]  = useState("");
  const [voyageYear, setVoyageYear] = useState(yearoptions[yearoptions.length - 1].value);
  const [casperId,   setCasperId]   = useState("");

  useEffect(() => {
    dispatch(fetchDocSuggestions());
    dispatch(fetchVesselLocations());
    dispatch(getSettlementDues(""));
  }, [dispatch]);

  const handleDOCChange = (value) => {
    setDocValue(value ?? "");
    setShipOwner(""); setVesselName(""); setIMONumber(""); setCasperId("");
    if (value) dispatch(fetchShipOwners(value));
  };

  const handleShipOwnerChange = (value) => {
    setShipOwner(value ?? "");
    setVesselName(""); setIMONumber(""); setCasperId("");
    if (value) dispatch(fetchVessels(value));
  };

  const handleVesselChange = (value) => {
    setVesselName(value ?? "");
    setCasperId("");
    const selected = vesselDetails.find(v => v.vessel_name === value);
    if (selected) {
      setIMONumber(selected.imo_number);
      dispatch(fetchCasperID({ imo_number: selected.imo_number, year: voyageYear }));
    }
  };

  const handleCasperIdChange = (value) => setCasperId(value ?? "");

  const handleIMOChange = (value) => {
    setIMONumber(value ?? "");
    setCasperId("");
    if (value) dispatch(fetchCasperID({ imo_number: value, year: voyageYear }));
  };

  const handleYearChange = (value) => {
    setVoyageYear(value);
    setCasperId("");
    if (iMONumber) dispatch(fetchCasperID({ imo_number: iMONumber, year: value }));
  };

  const handleReset = () => {
    dispatch(fetchDocSuggestions());
    setDocValue(""); setShipOwner(""); setVesselName("");
    setIMONumber(""); setCasperId("");
    setVoyageYear(yearoptions[yearoptions.length - 1].value);
    // Reset chart to default (no filters)
    dispatch(getSettlementDues(""));
  };

  const handleApply = () => {
    const params = {};
    if (docValue)   params.doc = docValue;
    if (shipOwner)  params.shipowner_name = shipOwner;
    if (vesselName) params.vessel_name = vesselName;
    if (iMONumber)  params.imo_number = iMONumber;
    if (casperId)   params.casper_id = casperId;
    if (voyageYear) params.requested_year = voyageYear;

    dispatch(fetchVesselLocations(params));
    // duesGraph in Redux updates automatically → BarChartSection re-renders
    dispatch(getSettlementDues(params));
  };

  return (
    <div className="compliance-dashboard-container">

      <div className="top-section-grid">

        {/* LEFT: Bar Chart */}
        <div className="chart-column">
          {/* duesGraph is the single source of truth; data is the initial/fallback prop */}
          <BarChartSection data={data} duesGraph={duesGraph} />
        </div>

        {/* RIGHT: Filters + Metric Cards */}
        <div className="cards-column">
          <div className="filters-bar">

            {docSuggestionsStatus === "loading" ? <Spin size="small" /> : (
              <Select className="filter-dropdown" placeholder="DOC" allowClear showSearch
                value={docValue || undefined} onChange={handleDOCChange}>
                {docSuggestions?.map(doc => <Option key={doc} value={doc}>{doc}</Option>)}
              </Select>
            )}

            {shipOwnersStatus === "loading" ? <Spin size="small" /> : (
              <Select className="filter-dropdown" placeholder="Ship Owner" allowClear showSearch
                value={shipOwner || undefined} onChange={handleShipOwnerChange} disabled={!docValue}>
                {shipOwners?.map(o => <Option key={o} value={o}>{o}</Option>)}
              </Select>
            )}

            {vesselStatus === "loading" ? <Spin size="small" /> : (
              <Select className="filter-dropdown" placeholder="Vessel Name" allowClear showSearch
                value={vesselName || undefined} onChange={handleVesselChange} disabled={!shipOwner}>
                {vesselDetails?.map(v => <Option key={v.vessel_name} value={v.vessel_name}>{v.vessel_name}</Option>)}
              </Select>
            )}

            <Select value={voyageYear} className="yearsel" onChange={handleYearChange}>
              {yearoptions.map(year => <Option key={year.value} value={year.value}>{year.label}</Option>)}
            </Select>

            <Select showSearch allowClear value={casperId || undefined}
              placeholder="Select Casper ID" className="casperid"
              onChange={handleCasperIdChange} onClear={() => setCasperId("")}>
              {casperID?.map(id => <Option key={id} value={id}>{id}</Option>)}
            </Select>

            <Button className="btn-reset-filter" onClick={handleReset}>Reset</Button>
            <Button className="btn-apply-filter" type="primary" onClick={handleApply}>Apply</Button>
          </div>

          <MetricCards data={data} />
        </div>
      </div>

      <div className="bottom-section-grid">
        <EmissionTrend data={data} />
        <ETSExposure
          data={data}
          iMONumber={iMONumber}
          voyageYear={voyageYear}
          casperId={casperId}
          casperID={casperID}
          vesselDetails={vesselDetails}
          vesselStatus={vesselStatus}
          onIMOChange={handleIMOChange}
          onYearChange={handleYearChange}
          onCasperIdChange={setCasperId}
        />
      </div>

    </div>
  );
};

export default ComplianceOverview;
